"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import Link from "next/link"

import { StatusPage } from "@/components/status-page"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { controlRadiusClass, fieldSurfaceClass } from "@/components/ui/field-surface"
import { cn } from "@/lib/utils"

const AIENT_SEQ = ["a", "i", "e", "n", "t"] as const
const KONAMI_SEQ = [
  "ArrowUp",
  "ArrowUp",
  "ArrowDown",
  "ArrowDown",
  "ArrowLeft",
  "ArrowRight",
  "ArrowLeft",
  "ArrowRight",
  "b",
  "a",
] as const

export default function NotFound() {
  const [issueVisible, setIssueVisible] = useState(false)
  const [whisperMode, setWhisperMode] = useState(false)
  const [fixGenerated, setFixGenerated] = useState(false)
  const [konamiActive, setKonamiActive] = useState(false)
  const [logLines, setLogLines] = useState([
    "aient.http — 404 captured with no associated incident.",
  ])

  const clickCountRef = useRef(0)
  const glimtBufferRef = useRef<string[]>([])
  const konamiBufferRef = useRef<string[]>([])

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      const key = e.key

      if (/^[a-zA-Z]$/.test(key)) {
        glimtBufferRef.current.push(key.toLowerCase())
        if (glimtBufferRef.current.length > AIENT_SEQ.length) {
          glimtBufferRef.current.shift()
        }
        const glimtMatch = AIENT_SEQ.every((k, i) => glimtBufferRef.current[i] === k)
        if (glimtMatch) {
          setIssueVisible(true)
        }
      }

      konamiBufferRef.current.push(key)
      if (konamiBufferRef.current.length > KONAMI_SEQ.length) {
        konamiBufferRef.current.shift()
      }
      const konamiMatch = KONAMI_SEQ.every((k, i) => konamiBufferRef.current[i] === k)
      if (konamiMatch && !konamiActive) {
        setKonamiActive(true)
        setTimeout(() => {
          window.location.href = "/dashboard"
        }, 2200)
      }
    },
    [konamiActive],
  )

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [handleKeyDown])

  const handleDotClick = () => {
    clickCountRef.current += 1
    if (clickCountRef.current === 5 && !whisperMode) {
      setWhisperMode(true)
      setLogLines((prev) => [
        ...prev,
        "ai.engine — User curiosity detected. Telemetry whisper mode enabled.",
      ])
    }
  }

  return (
    <StatusPage
      data-component="not-found-page"
      eyebrow={
        <div className="inline-flex items-center gap-2">
          <button
            type="button"
            onClick={handleDotClick}
            className="relative size-2 shrink-0 rounded-full bg-destructive shadow-[0_0_10px_color-mix(in_srgb,var(--destructive)_70%,transparent)]"
            aria-label="Route anomaly indicator"
            title="Click me a few times"
          >
            <span className="absolute -inset-1 rounded-full border border-destructive/45 opacity-80" />
          </button>
          <Badge variant="outline">404 · Route not observed</Badge>
        </div>
      }
      title={
        <span className="inline-flex flex-wrap items-baseline gap-3">
          <span>We lost the signal on this path.</span>
          <Badge>AIENT</Badge>
        </span>
      }
      description={
        whisperMode
          ? "Okay, okay… this route really doesn't exist. But we are watching."
          : "Aient links runtime telemetry with your code to surface production issues as they happen. But this URL never made it into the trace."
      }
      action={
        <Button asChild>
          <Link href="/dashboard">Back to dashboard</Link>
        </Button>
      }
    >
      <div className="group relative min-h-8">
        <div className="absolute -top-4 -right-4 z-2 size-11 cursor-pointer" aria-hidden="true" />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setFixGenerated(true)}
          className="absolute top-0 right-0 opacity-0 pointer-events-none group-hover:pointer-events-auto group-hover:opacity-100"
        >
          Auto-generate fix
        </Button>
      </div>

      <div
        className={cn(
          "max-h-28 overflow-hidden border border-border px-3 py-2 font-code text-[11px] text-muted-foreground",
          controlRadiusClass,
          fieldSurfaceClass,
        )}
      >
        {logLines.map((line, i) => (
          <div key={i} className={i > 0 ? "mt-0.5" : ""}>
            {line}
          </div>
        ))}
      </div>

      {issueVisible ? (
        <Card>
          <CardContent className="space-y-2 pt-6 text-xs">
            <Badge variant="outline">Issue #4041 · Synthetic</Badge>
            <div className="font-medium">Human navigated into the void</div>
            <p className="text-muted-foreground">
              Root cause: unmapped route · Suggested fix:{" "}
              <code className="font-code text-primary">redirect(&quot;/&quot;)</code>
            </p>
            <div className="flex flex-wrap gap-2">
              <Badge variant="outline">Open Slack thread</Badge>
              <Badge variant="outline">Create PR</Badge>
            </div>
          </CardContent>
        </Card>
      ) : null}

      {fixGenerated ? (
        <p className="text-[11px] text-muted-foreground">✅ Fix generated. ❌ Still a 404 though.</p>
      ) : null}

      {konamiActive ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/95">
          <Card className="max-w-90">
            <CardContent className="space-y-2 pt-6">
              <h2 className="font-brand-display text-lg font-normal">Root access granted</h2>
              <p className="text-sm text-muted-foreground">Just kidding. Redirecting you somewhere safer…</p>
            </CardContent>
          </Card>
        </div>
      ) : null}
    </StatusPage>
  )
}
